<template>
  <modal
    :title="modalTitle"
    :value="show"
    @cancel="closeModal"
    effect="fade/zoom"
    large
  >
    <Loader :is-visible="loading" />

    <div class="row">
      <div class="col-sm-12 form-group">
        <label>Nom *</label>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          :disabled="!hasWritePermission"
        />
        <small v-if="errors && errors.name" class="form-text text-danger">
          {{ errors.name }}</small
        >
      </div>
      <div v-if="isPaginatedContent" class="col-sm-12">
        <div
          v-for="(page, index) in form.pages"
          :key="`dynamic-content-page-${index}`"
          class="form-group"
        >
          <label
            >Page {{ index + 1 }} {{ index === 0 ? "*" : "" }}
            <button
              v-if="hasWritePermission && index > 0"
              type="button"
              class="btn btn-sm btn-secondary ml-05"
              @click="removeContentPage(index)"
            >
              <i class="fa fa-minus"></i>
            </button>
          </label>
          <quill-editor
            v-model="page.rawContent"
            :disabled="!hasWritePermission"
            @change="onPageEditorChange(index, $event)"
          >
          </quill-editor>
          <small
            v-if="errors && errors.pages && errors.pages[index]"
            class="form-text text-danger"
          >
            {{ errors.pages[index] }}</small
          >
        </div>

        <div v-if="hasWritePermission" class="form-group text-center">
          <button
            type="button"
            class="btn btn-secondary w-100"
            @click="addContentPage"
          >
            <i class="fa fa-plus"></i>
          </button>

          <small v-if="errors && errors.noPage" class="form-text text-danger">
            {{ errors.noPage }}</small
          >
        </div>
      </div>
      <div v-else class="col-sm-12 form-group">
        <label>Contenu *</label>
        <quill-editor
          v-model="form.rawContent"
          :disabled="!hasWritePermission"
          @change="onEditorChange($event)"
        >
        </quill-editor>
        <small v-if="errors && errors.content" class="form-text text-danger">
          {{ errors.content }}</small
        >
      </div>
      <div v-if="requiredMetadata.length > 0" class="col-sm-12">
        <div
          v-for="item in requiredMetadata"
          :key="`form-metadata-${item.key}`"
          class="form-group"
        >
          <label>{{ item.label }} *</label>
          <input
            v-model="form.metadata[item.key]"
            type="text"
            class="form-control"
            :disabled="!hasWritePermission"
          />
          <small
            v-if="errors && errors.metadata && errors.metadata[item.key]"
            class="form-text text-danger"
          >
            {{ errors.metadata[item.key] }}</small
          >
        </div>
      </div>
      <div class="col-sm-12">
        <div v-if="hasWritePermission" class="form-group">
          <el-switch
            v-model="form.hasPublishedPeriod"
            inactive-text=""
            active-text="Spécifier une période de publication"
            size="mini"
            @change="handleTogglePublishedPeriod"
          />
          <small
            v-if="errors && errors.publishedPeriod"
            class="form-text text-danger"
          >
            {{ errors.publishedPeriod }}</small
          >
        </div>
        <div v-if="form.hasPublishedPeriod" class="row">
          <div class="col-sm-6 form-group">
            <label>Début</label>
            <Datepicker
              v-if="hasWritePermission"
              v-model="form.publishedPeriod.start"
              format="dd.MM.yyyy"
              language="fr"
              input-class="form-control"
            ></Datepicker>
            <div v-else>
              {{
                form.publishedPeriod.start &&
                formatDate(form.publishedPeriod.start)
              }}
            </div>
          </div>
          <div class="col-sm-6 form-group">
            <label>Fin</label>
            <Datepicker
              v-if="hasWritePermission"
              v-model="form.publishedPeriod.end"
              format="dd.MM.yyyy"
              language="fr"
              input-class="form-control"
            ></Datepicker>
            <div v-else>
              {{
                form.publishedPeriod.end && formatDate(form.publishedPeriod.end)
              }}
            </div>
          </div>
        </div>
        <div v-if="canBeStatic && form.hasPublishedPeriod" class="form-group">
          <el-switch
            v-model="form.isStatic"
            inactive-text=""
            active-text="Actualité fixe"
            size="mini"
          />
          <small class="form-text">
            Si activé, l'actualité restera affichée le temps de la période de
            publication, sans que l'utilisateur puisse la supprimer.</small
          >
        </div>
      </div>
      <template v-if="form.userFilters">
        <hr class="w-100 mx-1" />
        <div class="col-sm-12 h5">
          Filtres coachs
          <button
            v-if="
              hasWritePermission &&
              (form.userFilters.emails.length > 0 ||
                form.userFilters.status.length > 0 ||
                form.userFilters.gender ||
                form.userFilters.zipcodes.length > 0 ||
                form.userFilters.b2bOnly)
            "
            type="button"
            class="btn btn-sm btn-outline-danger ml-05"
            v-tooltip="{ content: 'Supprimer les filtres', placement: 'top' }"
            @click="clearUserFilters"
          >
            <i class="fa fa-trash-o"></i>
          </button>
        </div>
        <div class="col-sm-6 form-group">
          <label>Coachs</label>
          <Multiselect
            v-if="hasWritePermission"
            v-model="form.userFilters.emails"
            :options="coachsOptions"
            label="label"
            track-by="label"
            placeholder="Choisir un ou plusieurs coachs"
            class="custom-multiselect"
            :multiple="true"
            :searchable="true"
            :loading="loadingSearchCoachs"
            :options-limit="10"
            :max-height="600"
            :hide-selected="true"
            @search-change="handleSearchCoachs"
          >
            <template v-slot:noOptions>Rechercher un coach</template>
            <template v-slot:noResult>Aucun coach trouvé</template>
          </Multiselect>
          <div
            v-else-if="
              !form.userFilters.emails || form.userFilters.emails.length === 0
            "
            class="text-muted"
          >
            Tous
          </div>
          <div v-else>
            {{ form.userFilters.emails.join(", ") }}
          </div>
        </div>
        <div class="col-sm-6 form-group">
          <label>Statut</label>
          <Multiselect
            v-if="hasWritePermission"
            v-model="form.userFilters.status"
            placeholder="Choisir un ou plusieurs statuts"
            :options="coachsStatusOptions"
            :multiple="true"
            label="label"
            track-by="label"
            class="custom-multiselect"
          >
          </Multiselect>
          <div
            v-else-if="
              !form.userFilters.status || form.userFilters.status.length === 0
            "
            class="text-muted"
          >
            Tous
          </div>
          <div v-else>
            {{
              form.userFilters.status
                .map((value) => {
                  const status = coachsStatusOptions.find(
                    (option) => option.value === value,
                  );

                  return status ? status.label : value;
                })
                .join(", ")
            }}
          </div>
        </div>
        <div class="col-sm-6 form-group">
          <label>Genre</label>
          <v-select
            v-if="hasWritePermission"
            v-model="form.userFilters.gender"
            :options="genderOptions"
            :reduce="({ value }) => value"
          >
          </v-select>
          <div v-else-if="!form.userFilters.gender" class="text-muted">
            Non précisé
          </div>
          <div v-else>
            {{ form.userFilters.gender }}
          </div>
        </div>
        <div class="col-sm-6 form-group">
          <label>Département</label>
          <Multiselect
            v-if="hasWritePermission"
            v-model="form.userFilters.zipcodes"
            :options="zipcodesOptions"
            label="label"
            track-by="label"
            placeholder="Choisir un ou plusieurs départements"
            class="custom-multiselect"
            :multiple="true"
            :searchable="true"
            :max-height="600"
            :hide-selected="true"
          ></Multiselect>
          <div
            v-else-if="
              !form.userFilters.zipcodes ||
              form.userFilters.zipcodes.length === 0
            "
            class="text-muted"
          >
            Tous
          </div>
          <div v-else>
            {{
              form.userFilters.zipcodes
                .map((value) => {
                  const zipcode = zipcodesOptions.find(
                    (option) => option.value === value.value,
                  );

                  return zipcode?.label || value;
                })
                .join(", ")
            }}
          </div>
        </div>
        <div class="col-sm-6 form-group">
          <label
            >Coachs B2B
            <el-popover
              placement="right"
              trigger="hover"
              :content="'Envoyer uniquement aux coachs qui sont attribués sur des séances Corporate'"
            >
              <template #reference><i class="fa fa-info-circle"></i></template>
            </el-popover>
          </label>
          <el-switch
            v-if="hasWritePermission"
            v-model="form.userFilters.b2bOnly"
            inactive-text=""
            active-text="B2B seulement"
            size="mini"
            class="d-block"
          />
          <div v-else>
            {{
              form.userFilters.b2bOnly
                ? "B2B seulement"
                : "Tous (pas seulement B2B)"
            }}
          </div>
        </div>
      </template>
    </div>

    <div slot="modal-footer" class="modal-footer">
      <template v-if="hasWritePermission">
        <button type="button" class="btn btn-default" @click="closeModal">
          Annuler
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="edit ? editDynamicContent() : createDynamicContent()"
        >
          {{ edit ? "Mettre à jour" : "Ajouter" }}
        </button>
      </template>
      <button
        v-else
        type="button"
        class="btn btn-primary"
        @click="$emit('close')"
      >
        Fermer
      </button>
    </div>
  </modal>
</template>

<script>
import debounce from "lodash.debounce";
import { mapGetters } from "vuex";
import Modal from "vue-strap/src/Modal";
import Loader from "@/components/utils/Loader";
import { quillEditor } from "vue-quill-editor";
import Datepicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";

export default {
  components: {
    Modal,
    Loader,
    quillEditor,
    Datepicker,
    Multiselect,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    dynamicContent: {
      type: Object,
      default: undefined,
    },

    type: {
      type: String,
      default: null,
    },

    writePermission: {
      type: String,
      default: null,
    },

    showUserFilters: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      form: {
        name: "",
        rawContent: "",
        content: "",
        pages: [{ rawContent: "", content: "" }],
        metadata: {},
        hasPublishedPeriod: false,
        publishedPeriod: {
          start: null,
          end: null,
        },
        isStatic: false,
        userFilters: null,
      },
      errors: {},
      requiredMetadata: [],
      loadingSearchCoachs: false,
      coachsOptions: [],
      genderOptions: [
        {
          label: "Non précisé",
          value: null,
        },
        {
          label: "Homme",
          value: "Homme",
        },
        {
          label: "Femme",
          value: "Femme",
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      coachsStatusOptions: "getCoachStatus",
      departments: "getDepartments",
    }),

    edit() {
      return !!this.dynamicContent?.id;
    },

    isPaginatedContent() {
      return (
        this.type === "TUTO_COACH_APP" || this.dynamicContent?.pages?.length > 0
      );
    },

    hasWritePermission() {
      return (
        this.writePermission &&
        this.hasPermission(this.$store.state.user, this.writePermission)
      );
    },

    modalTitle() {
      let title = this.edit ? "Modifier" : "Ajouter";

      switch (this.type) {
        case "COACH_SPECIAL_EVENT":
          title += " un événement";
          break;
        case "MAJ_COACH_APP":
          title += " une note de mise à jour";
          break;
        case "TUTO_COACH_APP":
          title += " un tutoriel";
          break;
        case "CLIENT_APP_NEWS":
          title += " une actualité";
          break;
        default:
          title += " un contenu dynamique";
      }

      return title;
    },

    canBeStatic() {
      return this.type === "CLIENT_APP_NEWS";
    },

    zipcodesOptions() {
      return this.departments.map((dpt) => ({
        label: dpt.code !== "all" ? `${dpt.code} - ${dpt.name}` : dpt.name,
        value: dpt.code,
      }));
    },
  },

  watch: {
    dynamicContent() {
      if (this.edit) {
        if (this.type !== this.dynamicContent.type) {
          this.$errorToast(
            "Le type du contenu dynamique ne correspond pas à celui attendu",
          );
          this.closeModal();
          return;
        }

        this.form = {
          name: this.dynamicContent.name,
          rawContent: this.dynamicContent.content || null,
          content: this.dynamicContent.content || null,
          pages: this.dynamicContent.pages
            ? this.dynamicContent.pages.map((page) => ({
                rawContent: page,
                content: page,
              }))
            : null,
          metadata: this.requiredMetadata.reduce((acc, { key }) => {
            acc[key] = this.dynamicContent.metadata
              ? this.dynamicContent.metadata[key]
              : "";

            return acc;
          }, {}),
          hasPublishedPeriod: !!this.dynamicContent.publishedPeriod,
          publishedPeriod: {
            start: this.dynamicContent.publishedPeriod?.start || null,
            end: this.dynamicContent.publishedPeriod?.end || null,
          },
          userFilters: this.showUserFilters
            ? {
                ...this.form.userFilters,
                emails: this.dynamicContent.userFilters?.emails || [],
                status:
                  this.dynamicContent.userFilters?.status?.map((status) => ({
                    label:
                      this.coachsStatusOptions.find(
                        (option) => option.value === status,
                      )?.label || status,
                    value: status,
                  })) || [],
                gender: this.dynamicContent.userFilters?.gender || null,
                zipcodes:
                  this.dynamicContent.userFilters?.zipcodes?.map((zipcode) => ({
                    label:
                      this.zipcodesOptions.find(
                        (option) => option.value === zipcode,
                      )?.label || zipcode,
                    value: zipcode,
                  })) || [],
                b2bOnly: this.dynamicContent.userFilters?.b2bOnly || false,
              }
            : null,
          isStatic: this.dynamicContent.isStatic || false,
        };
      } else {
        this.resetForm();
      }
    },

    type() {
      this.setRequiredMetadata();
    },

    showUserFilters() {
      if (this.showUserFilters) {
        this.form.userFilters = {
          emails: [],
          status: [],
          gender: null,
          zipcodes: [],
          b2bOnly: false,
        };
      } else {
        this.form.userFilters = null;
      }
    },
  },

  mounted() {
    this.setRequiredMetadata();

    if (this.showUserFilters) {
      this.form.userFilters = {
        emails: [],
        status: [],
        gender: null,
        zipcodes: [],
        b2bOnly: false,
      };
    }
  },

  methods: {
    resetForm() {
      this.form = {
        name: "",
        rawContent: "",
        content: "",
        pages: [{ rawContent: "", content: "" }],
        metadata: this.requiredMetadata.reduce((acc, key) => {
          acc[key] = "";

          return acc;
        }, {}),
        hasPublishedPeriod: false,
        publishedPeriod: {
          start: null,
          end: null,
        },
        userFilters: this.showUserFilters
          ? {
              emails: [],
              status: [],
              gender: null,
              zipcodes: [],
              b2bOnly: false,
            }
          : null,
      };
      this.errors = {};
    },

    closeModal() {
      this.$emit("close");
      this.resetForm();
    },

    onEditorChange({ html }) {
      this.form.content = html;
    },

    checkFields() {
      this.errors = {};

      if (!this.form.name) {
        this.errors.name = "Veuillez saisir un nom";
      }

      if (!this.isPaginatedContent && !this.form.content) {
        this.errors.content = "Veuillez saisir un contenu";
      }

      if (this.isPaginatedContent) {
        if (this.form.pages.length === 0) {
          this.errors.noPage = "Veuillez saisir au moins une page";
        }

        if (this.form.pages.some((p) => !p.content)) {
          this.errors.pages = this.form.pages.map((p, index) => {
            if (!p.content) {
              return index === 0
                ? "Veuillez saisir un contenu"
                : "Veuillez saisir un contenu ou supprimer la page";
            }

            return null;
          });
        }
      }

      if (this.requiredMetadata.length > 0) {
        this.requiredMetadata.forEach(({ key }) => {
          if (!this.form.metadata[key]) {
            this.errors.metadata = {
              ...this.errors.metadata,
              [key]: `Veuillez saisir une valeur pour ${key}`,
            };
          }
        });
      }

      if (this.form.hasPublishedPeriod) {
        if (
          !this.form.publishedPeriod.start &&
          !this.form.publishedPeriod.end
        ) {
          this.errors.publishedPeriod =
            "Veuillez renseigner au moins une date de début ou de fin";
        }
      }
    },

    async createDynamicContent() {
      this.checkFields();

      if (Object.values(this.errors).filter((error) => error).length > 0) {
        return;
      }

      try {
        this.loading = true;

        const { data } = await this.$api.post("/dynamic-contents", {
          ...this.form,
          type: this.type,
          content: !this.isPaginatedContent ? this.form.content : null,
          pages: this.isPaginatedContent
            ? this.form.pages?.map(({ content }) => content)
            : null,
          publishedPeriod: this.form.hasPublishedPeriod
            ? this.form.publishedPeriod
            : null,
          userFilters: this.form.userFilters
            ? {
                emails: this.form.userFilters.emails.map(
                  (email) => email.value,
                ),
                status: this.form.userFilters.status.map(
                  (status) => status.value,
                ),
                gender: this.form.userFilters.gender,
                zipcodes: this.form.userFilters.zipcodes.map(
                  (zipcode) => zipcode.value,
                ),
                b2bOnly: this.form.userFilters.b2bOnly,
              }
            : null,
        });

        this.$emit("created", data);
        this.closeModal();
        this.$successToast(
          "Contenu dynamique créé, rechargez pour voir les modifications",
        );
      } catch (e) {
        this.$errorToast("Impossible d'ajouter le contenu dynamique");
      } finally {
        this.loading = false;
      }
    },

    async editDynamicContent() {
      if (!this.dynamicContent?.id) {
        return;
      }

      this.checkFields();

      if (Object.values(this.errors).filter((error) => error).length > 0) {
        return;
      }

      try {
        this.loading = true;

        const { data } = await this.$api.put(
          `/dynamic-contents/${this.dynamicContent.id}`,
          {
            ...this.form,
            pages: this.form.pages?.map(({ content }) => content),
            publishedPeriod: this.form.hasPublishedPeriod
              ? this.form.publishedPeriod
              : null,
            userFilters: this.form.userFilters
              ? {
                  emails: this.form.userFilters.emails.map(
                    (email) => email.value,
                  ),
                  status: this.form.userFilters.status.map(
                    (status) => status.value,
                  ),
                  gender: this.form.userFilters.gender,
                  zipcodes: this.form.userFilters.zipcodes.map(
                    (zipcode) => zipcode.value,
                  ),
                  b2bOnly: this.form.userFilters.b2bOnly,
                }
              : null,
          },
        );

        this.$emit("updated", data);
        this.closeModal();
        this.$successToast("Contenu mis à jour");
      } catch (e) {
        this.$errorToast("Impossible de modifier le contenu dynamique");
      } finally {
        this.loading = false;
      }
    },

    onPageEditorChange(index, { html }) {
      this.form.pages[index].content = html;
    },

    addContentPage() {
      this.form.pages.push({ rawContent: "", content: "" });
    },

    removeContentPage(index) {
      this.form.pages.splice(index, 1);

      if (this.errors?.pages?.length > 0) {
        this.errors.pages.splice(index, 1);
      }
    },

    setRequiredMetadata() {
      switch (this.type) {
        case "MAJ_COACH_APP":
        case "TUTO_COACH_APP":
          this.requiredMetadata = [
            { label: "Numéro de version", key: "app_version" },
          ];
          break;
        default:
          this.requiredMetadata = [];
          break;
      }

      if (this.edit) {
        if (this.type !== this.dynamicContent.type) {
          this.$errorToast(
            "Le type du contenu dynamique ne correspond pas à celui attendu",
          );
          this.closeModal();
        }

        this.form.metadata = this.requiredMetadata.reduce((acc, key) => {
          acc[key] = this.dynamicContent.metadata
            ? this.dynamicContent.metadata[key]
            : "";

          return acc;
        }, {});
      } else {
        this.form.metadata = this.requiredMetadata.reduce((acc, key) => {
          acc[key] = "";

          return acc;
        }, {});
      }
    },

    handleSearchCoachs(search) {
      if (search !== "") {
        this.searchCoachs(search, this);
      }
    },

    searchCoachs: debounce(async (search, vm) => {
      if (!search || search.length < 3) {
        return;
      }

      try {
        vm.loadingSearchCoachs = true;

        const { data } = await vm.$api.post("/users/coachs/search", {
          query: search,
        });

        vm.coachsOptions = data.map((c) => ({
          label: `${c.coach ? `${c.coach.name}` : ""} (${c.email})`,
          value: c.email,
        }));
      } catch (error) {
        vm.$errorToast("Erreur lors de la recherche des coachs");
      } finally {
        vm.loadingSearchCoachs = false;
      }
    }, 300),

    clearUserFilters() {
      this.form.userFilters = {
        emails: [],
        status: [],
        gender: null,
        zipcodes: [],
        b2bOnly: false,
      };
    },

    handleTogglePublishedPeriod() {
      this.form.isStatic = false;
    },
  },
};
</script>
